import { marked, Renderer, type Tokens } from 'marked'

import type { ContentSnapshot, ContentSnapshotFull } from '@/types/content'

/**
 * True when `href` resolves to an http(s) URL on a different origin than the
 * current site. Relative links, in-page anchors, and non-http schemes
 * (mailto:, tel:) are treated as internal.
 */
function isExternalUrl(href: string): boolean {
  try {
    const url = new URL(href, window.location.href)
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      url.origin !== window.location.origin
    )
  } catch {
    return false
  }
}

/** Renderer that opens external links in a new tab with `noopener,noreferrer`. */
class ExternalLinkRenderer extends Renderer {
  link(token: Tokens.Link): string {
    const html = super.link(token)
    if (!isExternalUrl(token.href)) return html
    return html.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ')
  }
}

const renderer = new ExternalLinkRenderer()

/** Expanded image asset shape from rendered snapshots. */
export interface ExpandedImageAsset {
  assetId: string
  alt: string
  width?: number
  height?: number
  url: string
}

/**
 * Splits a markdown string into a flat list of items by stripping
 * `- ` or `* ` prefixes and filtering empty lines.
 */
export function parseMarkdownList(markdown: string | undefined | null): string[] {
  if (!markdown) return []
  return markdown
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => line.replace(/^[-*]\s+/, ''))
}

/**
 * Extracts the image URL from an expanded image asset.
 */
export function getImageUrl(image: ExpandedImageAsset | null | undefined): string | undefined {
  if (!image) return undefined
  return image.url
}

/**
 * Substitutes `{token}` placeholders in a CMS-authored template with values
 * from `vars`. Tokens with no matching key are left unreplaced (so a typo
 * surfaces visibly in the UI instead of becoming an empty string).
 */
export function interpolateTokens(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
  )
}

/**
 * Splits a markdown string into sections by any heading level (`#` – `######`).
 * Each section becomes `{ heading, body }` where body is the text
 * between headings (with leading/trailing whitespace trimmed).
 * Lines before the first heading are ignored.
 */
export function parseMarkdownSections(
  markdown: string | undefined | null
): { heading: string; body: string }[] {
  if (!markdown) return []
  const sections: { heading: string; body: string }[] = []
  const lines = markdown.split('\n')
  let currentHeading = ''
  let currentBody: string[] = []
  for (const line of lines) {
    const headingMatch = line.match(/^#{1,6}\s+(.+)$/)
    if (headingMatch) {
      if (currentHeading) {
        sections.push({ heading: currentHeading, body: currentBody.join('\n').trim() })
      }
      currentHeading = headingMatch[1].trim()
      currentBody = []
    } else if (currentHeading) {
      currentBody.push(line)
    }
  }
  if (currentHeading) {
    sections.push({ heading: currentHeading, body: currentBody.join('\n').trim() })
  }
  return sections
}

/** Parses a markdown string into a block HTML string for use with `v-html`. */
export function parseMarkdown(markdown: string | undefined | null): string {
  if (!markdown) return ''
  return marked.parse(markdown, { renderer }) as string
}

/** Parses a markdown string into an inline HTML string (no wrapping block elements) for use with `v-html`. */
export function parseMarkdownInline(markdown: string | undefined | null): string {
  if (!markdown) return ''
  return marked.parseInline(markdown, { renderer }) as string
}

/**
 * Per-field overlay of a CMS payload onto a local fallback. Starts from the
 * fallback shape, then copies any CMS field whose value is non-null and
 * non-empty-string. Used by both `contentStore.resolveDeep` (top-level
 * merge at every recursion) and the catalog-content path in
 * `bookingCommerce.getAddOnsPageContent` (which doesn't route through
 * `resolve()`). Centralized so the two paths can't drift in semantics.
 */
export function mergeCmsOverFallback<T>(cms: Partial<T> | undefined, fallback: T | undefined): T {
  const result: Record<string, unknown> = fallback
    ? { ...(fallback as Record<string, unknown>) }
    : {}
  if (cms) {
    for (const [k, v] of Object.entries(cms)) {
      if (v != null && v !== '') result[k] = v
    }
  }
  return result as T
}

export function extractFields<T>(
  snapshot: ContentSnapshot<T> | ContentSnapshotFull<T> | null,
  locale?: string
): T | null {
  if (!snapshot) return null
  if ('fields' in snapshot && snapshot.fields) return snapshot.fields
  if ('locales' in snapshot) {
    const loc = locale ?? 'en'
    return snapshot.locales[loc]?.fields ?? null
  }
  return null
}
